import { useState, useEffect } from "react";
import {
  subscribeToGallery,
  addGalleryItem,
  updateGalleryItem,
  deleteGalleryItem,
} from "../../services/firebase";
import {
  subscribeToCategories,
  uploadGalleryImage,
} from "../../services/firestore";
import {
  toastSuccess,
  toastError,
  toastPromise,
} from "../../components/admin/ToastProvider";
import { useConfirm } from "../../components/admin/ConfirmDialog";
import {
  GalleryGridSkeleton,
  StatsCardSkeleton,
} from "../../components/admin/Skeleton";
import {
  GalleryEmptyState,
  SearchEmptyState,
  ErrorState,
} from "../../components/admin/EmptyState";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Loader2,
  X,
  Image as ImageIcon,
  Upload,
  Link as LinkIcon,
  Star,
  Calendar,
  GripVertical,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const AdminGallery = () => {
  const [gallery, setGallery] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [form, setForm] = useState({
    image: "",
    title: "",
    description: "",
    categoryId: "",
    eventDate: "",
    featured: false,
  });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [imageSourceType, setImageSourceType] = useState("file");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileError, setFileError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedDescription, setExpandedDescription] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);

  // Confirmation dialog hook
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    let unsubscribeGallery;
    let unsubscribeCategories;

    const loadData = () => {
      try {
        unsubscribeGallery = subscribeToGallery((data) => {
          setGallery(data);
          setLoading(false);
          setError(null);
        });

        unsubscribeCategories = subscribeToCategories((data) => {
          setCategories(data);
        });
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Gagal memuat data. Silakan coba lagi.");
        setLoading(false);
      }
    };

    loadData();

    return () => {
      if (unsubscribeGallery) unsubscribeGallery();
      if (unsubscribeCategories) unsubscribeCategories();
    };
  }, []);

  const resetForm = () => {
    setForm({
      image: "",
      title: "",
      description: "",
      categoryId: "",
      eventDate: "",
      featured: false,
    });
    setEditing(null);
    setImageSourceType("file");
    setFileError("");
    setUploadProgress(0);
  };

  const handleBulkFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        return false;
      }
      return true;
    });
    setBulkFiles(validFiles);
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;

    setBulkUploading(true);
    setBulkUploadProgress(0);

    try {
      let uploadedCount = 0;

      for (let i = 0; i < bulkFiles.length; i++) {
        const file = bulkFiles[i];

        try {
          const imageUrl = await uploadGalleryImage(file, (progress) => {});

          const galleryData = {
            image: typeof imageUrl === "object" ? imageUrl.dataUrl : imageUrl,
            title: file.name.replace(/\.[^/.]+$/, ""),
            description: "",
            categoryId: "",
            categoryName: "",
            category: "other",
            eventDate: null,
            featured: false,
            displayOrder: gallery.length + uploadedCount,
          };

          await addGalleryItem(galleryData);
          uploadedCount++;
        } catch (fileError) {
          console.error("Error uploading file:", file.name, fileError);
        }

        setBulkUploadProgress(Math.round(((i + 1) / bulkFiles.length) * 100));
      }

      toastSuccess(`${uploadedCount} foto berhasil diupload!`);
      setBulkFiles([]);
      setShowBulkUploadModal(false);
    } catch (error) {
      console.error("Error bulk uploading:", error);
      toastError("Gagal mengupload beberapa foto");
    } finally {
      setBulkUploading(false);
    }
  };

  const resetBulkUploadForm = () => {
    setBulkFiles([]);
    setBulkUploadProgress(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let imageUrl = form.image;

      // Handle file upload
      if (imageSourceType === "file" && form.image instanceof File) {
        setUploading(true);
        setUploadProgress(0);

        const uploadPromise = new Promise((resolve, reject) => {
          uploadGalleryImage(form.image, (progress) => {
            setUploadProgress(progress);
          })
            .then(resolve)
            .catch(reject);
        });

        imageUrl = await uploadPromise;
        setUploading(false);
      }

      // Get category name
      const categoryName =
        categories.find((c) => c.id === form.categoryId)?.name ||
        form.categoryId;

      const galleryData = {
        image: typeof imageUrl === "object" ? imageUrl.dataUrl : imageUrl,
        title: form.title,
        description: form.description,
        categoryId: form.categoryId,
        categoryName: categoryName,
        category: categoryName?.toLowerCase() || "other",
        eventDate: form.eventDate ? new Date(form.eventDate) : null,
        featured: form.featured || false,
        displayOrder: editing ? editing.displayOrder : gallery.length,
      };

      // Use toast.promise for loading/success/error states
      await toastPromise(
        editing
          ? updateGalleryItem(editing.id, galleryData)
          : addGalleryItem(galleryData),
        {
          loading: editing ? "Memperbarui foto..." : "Menambahkan foto...",
          success: editing
            ? "Foto berhasil diperbarui!"
            : "Foto berhasil ditambahkan!",
          error: "Gagal menyimpan foto",
        },
      );

      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error("Error saving gallery item:", error);
      toastError("Gagal menyimpan foto: " + error.message);
      setUploading(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      image: item.image,
      title: item.title,
      description: item.description || "",
      categoryId: item.categoryId || "",
      eventDate: item.eventDate ? item.eventDate.split("T")[0] : "",
      featured: item.featured || false,
    });
    setImageSourceType("url");
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm(
      "Hapus foto ini? Tindakan tidak dapat dibatalkan.",
      {
        title: "Hapus Foto",
        type: "danger",
        confirmText: "Ya, Hapus",
        cancelText: "Batal",
      },
    );

    if (!confirmed) return;

    try {
      await toastPromise(deleteGalleryItem(id), {
        loading: "Menghapus foto...",
        success: "Foto berhasil dihapus!",
        error: "Gagal menghapus foto",
      });
      setSelectedItems((prev) => prev.filter((i) => i !== id));
    } catch (error) {
      console.error("Error deleting gallery item:", error);
      toastError("Gagal menghapus foto");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setFileError("Ukuran file harus kurang dari 5MB");
        return;
      }
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setFileError("Harap pilih file gambar");
        return;
      }
      setForm({ ...form, image: file });
      setFileError("");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await updateGalleryItem(item.id, { featured: !item.featured });
      toastSuccess(
        item.featured
          ? "Foto dihapus dari featured"
          : "Foto ditambahkan ke featured",
      );
    } catch (error) {
      console.error("Error toggling featured:", error);
      toastError("Gagal mengupdate status featured");
    }
  };

  const toggleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredGallery.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredGallery.map((item) => item.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    const confirmed = await confirm(
      `Hapus ${selectedItems.length} foto? Tindakan tidak dapat dibatalkan.`,
      {
        title: "Hapus Banyak Foto",
        type: "danger",
        confirmText: "Ya, Hapus Semua",
        cancelText: "Batal",
      },
    );

    if (!confirmed) return;

    try {
      await toastPromise(
        Promise.all(selectedItems.map((id) => deleteGalleryItem(id))),
        {
          loading: `Menghapus ${selectedItems.length} foto...`,
          success: `${selectedItems.length} foto berhasil dihapus!`,
          error: "Gagal menghapus beberapa foto",
        },
      );
      setSelectedItems([]);
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toastError("Gagal menghapus beberapa foto");
    }
  };

  const toggleDescription = (id) => {
    setExpandedDescription((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Filter gallery
  const filteredGallery = gallery.filter((item) => {
    const matchesSearch =
      item.title?.toLowerCase().includes(search.toLowerCase()) ||
      item.description?.toLowerCase().includes(search.toLowerCase());

    const matchesCategory =
      selectedCategory === "all" ||
      item.categoryId === selectedCategory ||
      item.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Stats
  const stats = {
    total: gallery.length,
    featured: gallery.filter((i) => i.featured).length,
    byCategory: categories.reduce((acc, cat) => {
      acc[cat.name] = gallery.filter((i) => i.categoryId === cat.id).length;
      return acc;
    }, {}),
  };

  // Get category name helper
  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat?.name || categoryId || "Other";
  };

  return (
    <div className="space-y-6">
      {/* Confirmation Dialog */}
      <ConfirmDialog />

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-2">
              <div className="w-48 h-8 rounded bg-white/5 animate-pulse" />
              <div className="w-64 h-4 rounded bg-white/5 animate-pulse" />
            </div>
            <div className="w-32 h-10 rounded-lg bg-white/5 animate-pulse" />
          </div>

          {/* Stats Skeleton */}
          <StatsCardSkeleton count={3} />

          {/* Filters Skeleton */}
          <div className="h-16 bg-white/5 border border-[#D4AF7A]/20 rounded-xl animate-pulse" />

          {/* Gallery Grid Skeleton */}
          <GalleryGridSkeleton count={8} />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <ErrorState
          title="Gagal Memuat Data"
          message={error}
          onRetry={() => window.location.reload()}
        />
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Gallery Management
              </h2>
              <p className="text-[#F7F5F2]/70">Kelola foto galeri henna</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
              >
                <Plus size={20} />
                Tambah Foto
              </button>
              <button
                onClick={() => {
                  resetBulkUploadForm();
                  setShowBulkUploadModal(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white/10 text-[#F7F5F2] font-semibold rounded-lg hover:bg-white/20 transition-colors border border-[#D4AF7A]/30"
              >
                <Upload size={20} />
                Bulk Upload
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#F7F5F2]/70">Total Foto</p>
                  <p className="text-2xl font-bold text-[#F7F5F2]">
                    {stats.total}
                  </p>
                </div>
                <ImageIcon className="w-8 h-8 text-[#D4AF7A]" />
              </div>
            </div>

            <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#F7F5F2]/70">Featured</p>
                  <p className="text-2xl font-bold text-[#D4AF7A]">
                    {stats.featured}
                  </p>
                </div>
                <Star className="w-8 h-8 text-[#D4AF7A]" />
              </div>
            </div>

            <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#F7F5F2]/70">Kategori</p>
                  <p className="text-2xl font-bold text-[#F7F5F2]">
                    {categories.length}
                  </p>
                </div>
                <GripVertical className="w-8 h-8 text-[#D4AF7A]" />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-4">
            <div className="flex flex-col gap-4 sm:flex-row">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#F7F5F2]/50" />
                <input
                  type="text"
                  placeholder="Cari foto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                />
              </div>

              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
              >
                <option value="all">Semua Kategori</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>

              {/* Bulk Actions */}
              {selectedItems.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 size={18} />
                  Hapus ({selectedItems.length})
                </button>
              )}
            </div>
          </div>

          {/* Gallery Grid */}
          {filteredGallery.length === 0 ? (
            search || selectedCategory !== "all" ? (
              <SearchEmptyState
                onClear={() => {
                  setSearch("");
                  setSelectedCategory("all");
                }}
              />
            ) : (
              <GalleryEmptyState
                onAdd={() => {
                  resetForm();
                  setShowModal(true);
                }}
              />
            )
          ) : (
            <>
              {/* Select All */}
              {filteredGallery.length > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="selectAll"
                    checked={
                      selectedItems.length === filteredGallery.length &&
                      filteredGallery.length > 0
                    }
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-[#D4AF7A]/30 text-[#D4AF7A] focus:ring-[#D4AF7A] bg-white/5"
                  />
                  <label
                    htmlFor="selectAll"
                    className="text-sm text-[#F7F5F2]/70 cursor-pointer"
                  >
                    Pilih Semua ({filteredGallery.length})
                  </label>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredGallery.map((item) => {
                  const isSelected = selectedItems.includes(item.id);
                  const isExpanded = expandedDescription[item.id];

                  return (
                    <div
                      key={item.id}
                      className={`bg-white/5 border rounded-xl overflow-hidden group transition-all ${
                        isSelected
                          ? "border-[#D4AF7A] shadow-lg shadow-[#D4AF7A]/10"
                          : "border-[#D4AF7A]/20"
                      }`}
                    >
                      {/* Image */}
                      <div className="relative overflow-hidden aspect-square">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                        />

                        {/* Featured Badge */}
                        {item.featured && (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-[#D4AF7A] text-black text-xs font-bold rounded-full flex items-center gap-1">
                            <Star size={12} fill="currentColor" />
                            Featured
                          </div>
                        )}

                        {/* Checkbox */}
                        <div className="absolute top-2 right-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectItem(item.id)}
                            className="w-5 h-5 rounded border-white/50 text-[#D4AF7A] focus:ring-[#D4AF7A] bg-white/20"
                          />
                        </div>

                        {/* Hover Actions */}
                        <div className="absolute inset-0 flex items-center justify-center gap-2 transition-opacity opacity-0 bg-black/60 group-hover:opacity-100">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 transition-colors rounded-lg bg-white/20 backdrop-blur-sm hover:bg-white/30"
                            title="Edit"
                          >
                            <Edit2 size={18} className="text-white" />
                          </button>
                          <button
                            onClick={() => toggleFeatured(item)}
                            className="p-2 transition-colors rounded-lg bg-white/20 backdrop-blur-sm hover:bg-white/30"
                            title={
                              item.featured ? "Hapus Featured" : "Set Featured"
                            }
                          >
                            <Star
                              size={18}
                              className={
                                item.featured
                                  ? "text-yellow-400 fill-yellow-400"
                                  : "text-white"
                              }
                            />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 transition-colors rounded-lg bg-red-500/20 backdrop-blur-sm hover:bg-red-500/30"
                            title="Hapus"
                          >
                            <Trash2 size={18} className="text-red-400" />
                          </button>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="p-3">
                        <div className="flex items-start justify-between mb-1">
                          <h3 className="font-semibold text-[#F7F5F2] text-sm truncate flex-1">
                            {item.title}
                          </h3>
                          {item.featured && (
                            <Star
                              size={14}
                              className="text-[#D4AF7A] flex-shrink-0 ml-1"
                              fill="currentColor"
                            />
                          )}
                        </div>

                        {/* Category Badge */}
                        <div className="mb-2">
                          <span className="inline-block px-2 py-0.5 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-full text-[#D4AF7A] text-xs">
                            {getCategoryName(item.categoryId)}
                          </span>
                        </div>

                        {/* Description */}
                        {item.description && (
                          <div className="text-xs text-[#F7F5F2]/60">
                            {isExpanded || item.description.length < 80 ? (
                              item.description
                            ) : (
                              <>
                                {item.description.substring(0, 80)}...
                                <button
                                  onClick={() => toggleDescription(item.id)}
                                  className="text-[#D4AF7A] ml-1 hover:underline"
                                >
                                  <ChevronDown size={12} className="inline" />
                                </button>
                              </>
                            )}
                            {item.description.length >= 80 && (
                              <button
                                onClick={() => toggleDescription(item.id)}
                                className="text-[#D4AF7A] ml-1 hover:underline"
                              >
                                {isExpanded ? (
                                  <>
                                    Show less
                                    <ChevronUp size={12} className="inline" />
                                  </>
                                ) : (
                                  <ChevronDown size={12} className="inline" />
                                )}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Event Date */}
                        {item.eventDate && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-[#F7F5F2]/50">
                            <Calendar size={12} />
                            {new Date(item.eventDate).toLocaleDateString(
                              "id-ID",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              },
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Add/Edit Modal */}
          {showModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
              <div className="bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-[#F7F5F2]">
                    {editing ? "Edit Foto" : "Tambah Foto Baru"}
                  </h2>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="p-2 transition-colors rounded-lg hover:bg-white/10"
                  >
                    <X className="w-5 h-5 text-[#F7F5F2]" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Image Source Toggle */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Sumber Gambar
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setImageSourceType("file")}
                        className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                          imageSourceType === "file"
                            ? "bg-[#D4AF7A] text-black"
                            : "bg-white/5 text-[#F7F5F2]"
                        }`}
                      >
                        <Upload size={16} className="inline mr-2" />
                        Upload File
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageSourceType("url")}
                        className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                          imageSourceType === "url"
                            ? "bg-[#D4AF7A] text-black"
                            : "bg-white/5 text-[#F7F5F2]"
                        }`}
                      >
                        <LinkIcon size={16} className="inline mr-2" />
                        URL
                      </button>
                    </div>
                  </div>

                  {/* Image Input */}
                  {imageSourceType === "file" ? (
                    <div>
                      <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                        Upload Gambar *
                      </label>
                      <div className="border-2 border-dashed border-[#D4AF7A]/30 rounded-lg p-6 text-center hover:border-[#D4AF7A]/50 transition-colors">
                        <input
                          type="file"
                          required={!editing}
                          accept="image/*"
                          onChange={handleFileChange}
                          className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#D4AF7A] file:text-black file:cursor-pointer"
                        />
                        {fileError && (
                          <p className="mt-2 text-xs text-red-400">
                            {fileError}
                          </p>
                        )}
                        {uploading && (
                          <div className="mt-3">
                            <div className="flex items-center justify-center gap-2 text-[#D4AF7A] mb-2">
                              <Loader2 size={16} className="animate-spin" />
                              <span className="text-sm">
                                Uploading... {uploadProgress}%
                              </span>
                            </div>
                            <div className="w-full h-2 rounded-full bg-white/10">
                              <div
                                className="bg-[#D4AF7A] h-2 rounded-full transition-all"
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                        URL Gambar *
                      </label>
                      <input
                        type="url"
                        required={!editing}
                        value={form.image}
                        onChange={(e) =>
                          setForm({ ...form, image: e.target.value })
                        }
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                      />
                      {(form.image || editing?.image) && (
                        <div className="mt-2 rounded-lg overflow-hidden border border-[#D4AF7A]/20">
                          <img
                            src={form.image || editing?.image}
                            alt="Preview"
                            className="object-cover w-full h-40"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Judul *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.title}
                      onChange={(e) =>
                        setForm({ ...form, title: e.target.value })
                      }
                      placeholder="Contoh: Henna Wedding - Sarah & Ahmad"
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Kategori *
                    </label>
                    <select
                      required
                      value={form.categoryId}
                      onChange={(e) =>
                        setForm({ ...form, categoryId: e.target.value })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                    >
                      <option value="">Pilih kategori</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Deskripsi
                    </label>
                    <textarea
                      value={form.description}
                      onChange={(e) =>
                        setForm({ ...form, description: e.target.value })
                      }
                      placeholder="Deskripsi singkat tentang foto ini..."
                      rows={3}
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                    />
                  </div>

                  {/* Event Date */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Tanggal Acara
                    </label>
                    <input
                      type="date"
                      value={form.eventDate}
                      onChange={(e) =>
                        setForm({ ...form, eventDate: e.target.value })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>

                  {/* Featured Toggle */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={form.featured}
                      onChange={(e) =>
                        setForm({ ...form, featured: e.target.checked })
                      }
                      className="w-4 h-4 rounded border-[#D4AF7A]/30 text-[#D4AF7A] focus:ring-[#D4AF7A] focus:ring-offset-0 bg-white/5"
                    />
                    <label
                      htmlFor="featured"
                      className="text-sm text-[#F7F5F2] flex items-center gap-2 cursor-pointer"
                    >
                      <Star size={16} className="text-[#D4AF7A]" />
                      Tampilkan sebagai foto unggulan (Featured)
                    </label>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-[#D4AF7A]/20">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="flex-1 px-4 py-2.5 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors"
                      disabled={submitting || uploading}
                    >
                      Batal
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || uploading}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Menyimpan...
                        </>
                      ) : (
                        <>
                          <Check size={18} />
                          {editing ? "Simpan" : "Tambah"}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Bulk Upload Modal */}
          {showBulkUploadModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
              <div className="bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-[#F7F5F2]">
                    Bulk Upload Foto
                  </h2>
                  <button
                    onClick={() => {
                      setShowBulkUploadModal(false);
                      resetBulkUploadForm();
                    }}
                    className="p-2 transition-colors rounded-lg hover:bg-white/10"
                  >
                    <X className="w-5 h-5 text-[#F7F5F2]" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Pilih Foto (Multiple)
                    </label>
                    <div className="border-2 border-dashed border-[#D4AF7A]/30 rounded-lg p-6 text-center hover:border-[#D4AF7A]/50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBulkFileChange}
                        multiple
                        className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#D4AF7A] file:text-black file:cursor-pointer"
                      />
                    </div>
                    <p className="text-xs text-[#F7F5F2]/50 mt-2">
                      Maks 5MB per file. Informasi bisa ditambahkan nanti dengan
                      klik Edit.
                    </p>
                  </div>

                  {bulkFiles.length > 0 && (
                    <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-lg p-4">
                      <p className="text-sm text-[#F7F5F2] mb-2">
                        {bulkFiles.length} foto dipilih:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {bulkFiles.slice(0, 10).map((file, index) => (
                          <div
                            key={index}
                            className="text-xs text-[#F7F5F2]/70 bg-white/5 px-2 py-1 rounded"
                          >
                            {file.name}
                          </div>
                        ))}
                        {bulkFiles.length > 10 && (
                          <div className="text-xs text-[#F7F5F2]/50 px-2 py-1">
                            +{bulkFiles.length - 10} lainnya
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {bulkUploading && (
                    <div className="mt-3">
                      <div className="flex items-center justify-center gap-2 text-[#D4AF7A] mb-2">
                        <Loader2 size={16} className="animate-spin" />
                        <span className="text-sm">
                          Mengupload... {bulkUploadProgress}%
                        </span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-white/10">
                        <div
                          className="bg-[#D4AF7A] h-2 rounded-full transition-all"
                          style={{ width: `${bulkUploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4 border-t border-[#D4AF7A]/20">
                    <button
                      type="button"
                      onClick={() => {
                        setShowBulkUploadModal(false);
                        resetBulkUploadForm();
                      }}
                      className="flex-1 px-4 py-2.5 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors"
                      disabled={bulkUploading}
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkUpload}
                      disabled={bulkUploading || bulkFiles.length === 0}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {bulkUploading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Mengupload...
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          Upload{" "}
                          {bulkFiles.length > 0 ? `(${bulkFiles.length})` : ""}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminGallery;
